import { useLocation } from 'react-router-dom';
import React from 'react';
import { DateTimePicker } from '@mantine/dates';
import { Button, TextInput, Select, Collapse } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { useCallback, useEffect, useState } from 'react';
import { Language } from '../../../languages/languageHandler';
import ChooseEntityModal from '../choose-entity-modal';
import Input from '../../../components/inputs';
import { getStartOfDay, getEndOfDay, toMoment, formatTimeNSecLabel } from '../../../utils/date';
import { clearFilters, setHelpDeskFilters } from '../../../client-redux/filterbar/actionCreators';
import { TICKETSTATUS } from '../../pages/ticket-list';
import classes from '../../../components/util/date-time-picker.module.scss';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

export interface HelpdeskFilterBarFieldsData {
  entity?: number;
  etid?: number;
  eid?: number;
  sn?: string;
  name?: string;
  nif?: number;
  type?: string;
  ticketId?: number;
  ticketStatus?: string;
  contractId?: number;
  eventCode?: number;
  clientId?: number;
  productCode?: string;
  devId?: number;
  codArm?: number;
  currentClosedStatus?: string;
  beginDateTime?: Date | string | null;
  endDateTime?: Date | string;
}

export interface HelpdeskFilterBarFieldsUsage {
  entity?: boolean;
  lid?: boolean;
  lidInput?: boolean;
  etid?: boolean;
  eid?: boolean;
  sn?: boolean;
  name?: boolean;
  nif?: boolean;
  type?: boolean;
  ticketId?: boolean;
  ticketStatus?: boolean;
  contractId?: boolean;
  eventCode?: boolean;
  clientId?: boolean;
  productCode?: boolean;
  devId?: boolean;
  codArm?: boolean;
  currentClosedStatus?: boolean;
  beginDateTime?: boolean;
  endDateTime?: boolean;
}

interface HelpdeskFilterBarProps {
  /**
   * Triggered by the search button or enter key
   */
  onSearch: () => void;
  /**
   * Triggered by data change
   */
  onDataChange: (data: HelpdeskFilterBarFieldsData) => void;
  fieldsUsage: HelpdeskFilterBarFieldsUsage;
  initialData?: Partial<HelpdeskFilterBarFieldsData>;
  xlsReport?: () => void;
  //pdfReport?: () => void;
  addButton?: () => void;
}

const HelpdeskFilterBar = ({
  onSearch,
  onDataChange,
  fieldsUsage,
  initialData,
  xlsReport,
  //pdfReport,
  addButton
}: HelpdeskFilterBarProps) => {
  const location = useLocation();
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const storeData = useSelector<AppState, HelpdeskFilterBarFieldsData>(state => state.filters.helpDeskFilterBar);
  const dispatch = useDispatch();
  const [usage] = useState<HelpdeskFilterBarFieldsUsage>(fieldsUsage);
  const [data, setData] = useState<HelpdeskFilterBarFieldsData>({});
  const [chooseEntityModal, setChooseEntityModal] = useState<boolean>(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState<boolean>(false);

  const entityTypes = [
    { value: '', label: lang.ENTITY_TYPE_NULL },
    { value: '0', label: lang.ENTITY_TYPE_0 },
    { value: '1', label: lang.ENTITY_TYPE_1 },
    { value: '2', label: lang.ENTITY_TYPE_2 },
    { value: '3', label: lang.ENTITY_TYPE_3 }
  ];

  const queryParams = new URLSearchParams(location.search);
  const [rawInputs, setRawInputs] = useState<Partial<Record<keyof HelpdeskFilterBarFieldsData, string>>>({});

  useEffect(() => {
    setDefaultData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAndCleanFilterParameter = (param: string): string | null => {
    const value = queryParams.get(param);
    if (value !== null) {
      queryParams.delete(param);
      const newSearch = queryParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
    return value;
  };

  const setDefaultData = (useStore: boolean, ignoreQueryParams = false) => {
    let newData: HelpdeskFilterBarFieldsData = { ...storeData };
    if (usage.beginDateTime) {
      if (!newData.beginDateTime) {
        if (initialData) {
          newData.beginDateTime = initialData.beginDateTime;
        } else {
          newData.beginDateTime = getStartOfDay();
        }
      }
    }

    if (usage.endDateTime) {
      if (!newData.endDateTime) {
        if (initialData) {
          newData.endDateTime = initialData.endDateTime;
        } else {
          newData.endDateTime = getEndOfDay();
        }
      }
    }

    if (usage.ticketStatus) {
      if (typeof storeData.ticketStatus !== 'undefined') {
        newData.ticketStatus = storeData.ticketStatus;
      } else {
        newData.ticketStatus = storeData.ticketStatus ?? initialData?.ticketStatus ?? TICKETSTATUS.OPEN;
      }
    }

    if (!ignoreQueryParams) {
      const eidParam = getAndCleanFilterParameter('eid');
      const entidParam = getAndCleanFilterParameter('entid');
      const snParam = getAndCleanFilterParameter('sn');
      const devIdParam = getAndCleanFilterParameter('devid');
      const codArmParam = getAndCleanFilterParameter('codArm');
      const productCodeParam = getAndCleanFilterParameter('productCode');
      const ticketStatusParam = getAndCleanFilterParameter('ticketStatus');

      if (eidParam && !isNaN(Number(eidParam))) newData.eid = Number(eidParam);
      if (entidParam && !isNaN(Number(entidParam))) newData.entity = Number(entidParam);
      if (snParam) newData.sn = snParam;
      if (devIdParam && !isNaN(Number(devIdParam))) newData.devId = Number(devIdParam);
      if (codArmParam && !isNaN(Number(codArmParam))) newData.codArm = Number(codArmParam);
      if (productCodeParam) newData.productCode = productCodeParam;
      if (ticketStatusParam) newData.ticketStatus = ticketStatusParam;
    }

    handleSearch(newData);
    setIsAdvancedSearchOpen(false);
  };

  const handleSearch = useCallback(
    (newData?: HelpdeskFilterBarFieldsData) => {
      if (!newData) {
        onSearch();
      } else {
        onDataChange(newData);
        setData(newData);
        dispatch(setHelpDeskFilters(newData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearch, onDataChange]
  );

  const handleNumericInput = (field: keyof HelpdeskFilterBarFieldsData, value: string | number | null) => {
    const inputValue = value?.toString() ?? '';
    setRawInputs(prev => ({ ...prev, [field]: inputValue }));

    const numericValue = inputValue === '' || isNaN(Number(inputValue)) ? undefined : Number(inputValue);

    handleSearch({ ...data, [field]: numericValue });
  };

  const chooseEntid = (entid: number, name?: string) => {
    if (entid && data?.entity !== entid) {
      const updatedData: HelpdeskFilterBarFieldsData = {
        ...data,
        entity: entid
      };
      if (name) updatedData.name = name;
      handleSearch(updatedData);
    }
    toggleChooseEntityModal();
  };

  /* Entity handle */
  const handleEntitySelect = (value: number | null) => {
    handleNumericInput('entity', value);
  };

  /* Etid handle */
  const handleEtid = (value: number | null) => {
    handleNumericInput('etid', value);
  };

  /* EID handle */
  const handleEID = (value: number | null) => {
    handleNumericInput('eid', value);
  };

  /* Serial Number handle */
  const handleSN = (value: string | null) => {
    handleSearch({
      ...data,
      sn: value ?? ''
    });
    setRawInputs(prev => ({ ...prev, sn: value ?? '' }));
  };

  /* EventCode handle  */
  const handleEventCode = (value: string) => {
    handleNumericInput('eventCode', value);
  };

  /* Name handle */
  const handleName = (value: string | null) => {
    handleSearch({
      ...data,
      name: value ?? ''
    });
  };

  /* NIF handle */
  const handleNIF = (value: number | null) => {
    handleNumericInput('nif', value);
  };

  /* Type handle */
  const handleType = (value: string | null) => {
    handleSearch({
      ...data,
      type: value ?? ''
    });
  };

  /* Ticket handle */
  const handleTicket = (value: number | null) => {
    handleNumericInput('ticketId', value);
  };

  /* Ticket Status handle */
  const handleTicketStatus = (value: string | null) => {
    handleSearch({
      ...data,
      ticketStatus: value ?? ''
    });
  };

  /* Contract handle */
  const handleContract = (value: string) => {
    handleNumericInput('contractId', value);
  };

  /* DevId handle */
  const handleDevId = (value: string) => {
    handleNumericInput('devId', value);
  };

  /* CodArm handle */
  const handleCodArm = (value: string) => {
    handleNumericInput('codArm', value);
  };

  /* ProductCode handle */
  const handleProductCode = (value: string | null) => {
    handleSearch({
      ...data,
      productCode: value ?? ''
    });
  };

  /* Begin handle */
  const handleBeginDateChange = (date: string | null) => {
    let newDate: Date | undefined;
    if (date && date !== 'Invalid Date') {
      const picked = toMoment(date);
      if (picked.isValid()) {
        if (data?.endDateTime && picked.isAfter(toMoment(data?.endDateTime))) return;
        newDate = picked.toDate();
      }
    }
    //setData(prev => ({ ...prev, beginDateTime: newDate }));
    handleSearch({ ...data, beginDateTime: newDate });
  };

  /* End handle */
  const handleEndDateChange = (date: string | null) => {
    let newDate: Date | undefined;
    if (date && date !== 'Invalid Date') {
      const picked = toMoment(date);
      if (picked.isValid()) {
        if (data?.beginDateTime && picked.isBefore(toMoment(data?.beginDateTime))) return;
        newDate = picked.toDate();
      }
    }
    //setData(prev => ({ ...prev, endDateTime: newDate }));
    handleSearch({ ...data, endDateTime: newDate });
  };

  const toggleChooseEntityModal = () => {
    setChooseEntityModal(!chooseEntityModal);
  };

  const toggleAdvancedSearch = useCallback(() => {
    setIsAdvancedSearchOpen(prev => !prev);
  }, []);

  const orderedFilterIds: string[] = [
    'entity',
    'etid',
    'eid',
    'sn',
    'name',
    'nif',
    'type',
    'ticketId',
    'ticketStatus',
    'contractId',
    'eventCode',
    'clientId',
    'productCode',
    'devId',
    'codArm',
    'currentClosedStatus',
    'beginDateTime',
    'endDateTime'
  ];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  // Helper function to return a filter component
  const getFilterComponent = (filterId: string): JSX.Element | null => {
    switch (filterId) {
      case 'entity':
        return usage.entity ? (
          <Input
            label={lang.ENTITY_ID}
            value={data.entity ?? ''}
            onChange={event => {
              const raw = event.target.value;
              handleEntitySelect(raw === '' ? null : Number(raw));
            }}
            isEditable
            showName={false}
            search={toggleChooseEntityModal}
          />
        ) : null;

      case 'etid':
        return usage.etid ? (
          <TextInput
            label={lang.GLOBAL_TYPE}
            value={data.etid ?? ''}
            onChange={event => handleEtid(event.target.value ? Number(event.target.value) : null)}
            onKeyDown={handleKeyDown}
            error={rawInputs.etid && isNaN(Number(rawInputs.etid)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'sn':
        return usage.sn ? (
          <TextInput
            label="SN"
            value={data.sn ?? ''}
            onChange={event => handleSN(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : null;

      case 'eid':
        return usage.eid ? (
          <TextInput
            label="EID"
            value={data.eid?.toString() ?? ''}
            onChange={event => handleEID(event.target.value ? Number(event.target.value) : null)}
            onKeyDown={handleKeyDown}
            error={rawInputs.eid && isNaN(Number(rawInputs.eid)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'eventCode':
        return usage.eventCode ? (
          <TextInput
            label={lang.EVENT_CODE}
            value={data.eventCode?.toString() ?? ''}
            onChange={event => handleEventCode(event.target.value)}
            onKeyDown={handleKeyDown}
            error={rawInputs.eventCode && isNaN(Number(rawInputs.eventCode)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'name':
        return usage.name ? (
          <TextInput
            label={lang.GLOBAL_NAME}
            value={data.name ?? ''}
            onChange={event => handleName(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : null;

      case 'nif':
        return usage.nif ? (
          <TextInput
            label="NIF"
            value={data.nif ?? ''}
            onChange={event => handleNIF(event.target.value ? Number(event.target.value) : null)}
            onKeyDown={handleKeyDown}
            error={rawInputs.nif && isNaN(Number(rawInputs.nif)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'type':
        return usage.type ? (
          <Select
            label={lang.GLOBAL_TYPE}
            data={entityTypes}
            placeholder={lang.GLOBAL_ALL}
            value={data.type ?? ''}
            onChange={value => handleType(String(value))}
          />
        ) : null;

      case 'ticketId':
        return usage.ticketId ? (
          <TextInput
            label="Ticket"
            value={data.ticketId ?? ''}
            onChange={event => handleTicket(event.target.value ? Number(event.target.value) : null)}
            onKeyDown={handleKeyDown}
            error={rawInputs.ticketId && isNaN(Number(rawInputs.ticketId)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'ticketStatus':
        return usage.ticketStatus ? (
          <Select
            label={lang.HELPDESK_TICKET_STATUS}
            data={[
              { value: TICKETSTATUS.ALL, label: lang.GLOBAL_ALL },
              { value: TICKETSTATUS.OPEN, label: lang.HELPDESK_TICKET_STATUS_0 },
              { value: TICKETSTATUS.CLOSE, label: lang.HELPDESK_TICKET_STATUS_1 }
            ]}
            value={data.ticketStatus ?? TICKETSTATUS.OPEN}
            onChange={value => handleTicketStatus(value as TICKETSTATUS)}
          />
        ) : null;

      case 'contractId':
        return usage.contractId ? (
          <TextInput
            label={lang.HELPDESK_CONTRACTS_ID}
            value={data.contractId?.toString() ?? ''}
            onChange={event => handleContract(event.target.value)}
            onKeyDown={handleKeyDown}
            error={rawInputs.contractId && isNaN(Number(rawInputs.contractId)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'productCode':
        return usage.productCode ? (
          <TextInput
            label={lang.HELPDESK_PRODUCT_CODE}
            value={data.productCode ?? ''}
            onChange={event => handleProductCode(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && handleSearch()}
            error={
              rawInputs.productCode && isNaN(Number(rawInputs.productCode)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`
            }
          />
        ) : null;

      case 'devId':
        return usage.devId ? (
          <TextInput
            label="DevId"
            value={data.devId?.toString() ?? ''}
            onChange={event => handleDevId(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && handleSearch()}
            error={rawInputs.devId && isNaN(Number(rawInputs.devId)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'codArm':
        return usage.codArm ? (
          <TextInput
            label={lang.GLOBAL_WAREHOUSE}
            value={data.codArm?.toString() ?? ''}
            onChange={event => handleCodArm(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && handleSearch()}
            error={rawInputs.codArm && isNaN(Number(rawInputs.codArm)) && `${lang.GLOBAL_ONLY_NUMERIC_VALUES}`}
          />
        ) : null;

      case 'beginDateTime':
        return usage?.beginDateTime ? (
          <DateTimePicker
            classNames={{
              day: classes.day,
              wrapper: classes.wrapper
            }}
            clearable
            label={lang.TRANSACTION_INITIAL_DATE}
            value={data?.beginDateTime}
            defaultValue={data?.beginDateTime ? formatTimeNSecLabel(data?.beginDateTime) : undefined}
            onChange={handleBeginDateChange}
            maxDate={data?.endDateTime ?? undefined}
            placeholder={lang.GLOBAL_PICK_A_DATE}
            valueFormat="DD/MM/YYYY HH:mm"
            timePickerProps={{
              withDropdown: true,
              popoverProps: { withinPortal: false },
              format: '24h',
              classNames: {
                input: classes.timeInput,
                field: classes.timeField,
                control: classes.timeControl
              }
            }}
          />
        ) : null;

      case 'endDateTime':
        return usage?.endDateTime ? (
          <DateTimePicker
            classNames={{
              day: classes.day
            }}
            clearable
            label={lang.TRANSACTION_END_DATE}
            value={data?.endDateTime}
            defaultValue={data?.endDateTime ? formatTimeNSecLabel(data?.endDateTime) : undefined}
            onChange={handleEndDateChange}
            minDate={data?.beginDateTime ?? undefined}
            maxDate={getEndOfDay()}
            placeholder={lang.GLOBAL_PICK_A_DATE}
            valueFormat="DD/MM/YYYY HH:mm"
            timePickerProps={{
              withDropdown: true,
              popoverProps: { withinPortal: false },
              format: '24h',
              classNames: {
                input: classes.timeInput,
                field: classes.timeField,
                control: classes.timeControl
              }
            }}
          />
        ) : null;

      default:
        return null;
    }
  };

  const mainFiltersToRender: JSX.Element[] = [];
  const advancedFiltersToRender: JSX.Element[] = [];
  let renderedFilterCount = 0;

  orderedFilterIds.forEach(id => {
    const filterComponent = getFilterComponent(id);
    if (filterComponent) {
      if (renderedFilterCount < 4) {
        mainFiltersToRender.push(<div key={id}>{filterComponent}</div>);
        renderedFilterCount++;
      } else {
        advancedFiltersToRender.push(<div key={id}>{filterComponent}</div>);
      }
    }
  });

  // Determine if the advanced section should be visible/toggleable
  const hasAdvancedFilters = advancedFiltersToRender.length > 0;
  const showAdvancedSearchButton = hasAdvancedFilters;

  return (
    <div className="helpdesk-filterBar-container">
      <ChooseEntityModal
        lang={lang}
        isOpen={chooseEntityModal}
        hideBtn={toggleChooseEntityModal}
        chooseEntid={chooseEntid}
      />

      {/* Main row: inputs + buttons */}
      <div className="filterbar-row">
        <div className="filters main-row">{mainFiltersToRender}</div>

        <div className="buttons">
          {/* Open filters */}
          {showAdvancedSearchButton && (
            <Button radius="md" onClick={toggleAdvancedSearch}>
              {isAdvancedSearchOpen ? (
                <Icon.IconChevronsUp stroke={2} size={28} />
              ) : (
                <Icon.IconChevronsDown stroke={2} size={28} />
              )}
            </Button>
          )}

          {/* Clear filters */}
          <Button
            radius="md"
            onClick={() => {
              dispatch(clearFilters());
              const resetFilters: HelpdeskFilterBarFieldsData = {
                ticketStatus: TICKETSTATUS.OPEN
              };
              setData(resetFilters);
              onDataChange(resetFilters);
              handleSearch(resetFilters);
            }}
          >
            <Icon.IconFilterX stroke={2} size={28} />
          </Button>

          {/* Search */}
          <Button radius="md" className="search-button" onClick={onSearch}>
            <Icon.IconRefresh stroke={2} size={28} />
          </Button>

          {/* Excel */}
          {xlsReport && (
            <Button radius="md" onClick={xlsReport}>
              <Icon.IconFileTypeXls stroke={2} />
            </Button>
          )}

          {/* Add Button */}
          {addButton && (
            <Button radius="md" onClick={addButton}>
              <Icon.IconPlus stroke={2} />
            </Button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {hasAdvancedFilters && (
        <Collapse in={isAdvancedSearchOpen}>
          <div className="filters advanced-row">{advancedFiltersToRender}</div>
        </Collapse>
      )}
    </div>
  );
};

export default HelpdeskFilterBar;
